import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { depascalize, listFiltersService, ListTableSort, SortOrder } from "sprint-common-web";

import { TrailerFilter } from "../../../../common/components/trailer-info/TrailerFilter";
import { FileRecordAssociation } from "../../../../common/types/FileRecordAssociation";
import { FileRecordBuzzNotification } from "../../../../common/types/FileRecordBuzzNotification";
import { FileRecordStatus } from "../../../../common/types/FileRecordStatus";
import { RecordType } from "../../../../common/types/RecordType";
import { FileRecordListingContext } from "../context/FileRecordListingContext";

export interface FileRecordListingFilters {
    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: SortOrder;
    content?: string;
    deliveryFileName?: string;
    filePath?: string;
    deliveryRef?: string;
    statuses?: number[];
    associations?: number[];
    notifications?: number[];
    trailers?: number[];
    recordTypes?: number[];
    createdDateFrom?: Date;
    createdDateTo?: Date;
}

const selectedStatuses = [
    FileRecordStatus.Created,
    FileRecordStatus.Qc,
    FileRecordStatus.Editing,
    FileRecordStatus.Rejected,
    FileRecordStatus.WaitingForClientResponse,
    FileRecordStatus.Destroyed
];

const selectedRecordTypes = [RecordType.Video, RecordType.Audio, RecordType.Subtitle];
const selectedNotifications = [FileRecordBuzzNotification.Approved, FileRecordBuzzNotification.Unapproved];
const selectedAssociations = [FileRecordAssociation.Associated, FileRecordAssociation.NonAssociated];
const selectedTrailers = [TrailerFilter.Trailer, TrailerFilter.NonTrailer];

const defaultFilters: FileRecordListingFilters = {
    page: 1,
    pageSize: 50,
    sortBy: "updatedDate",
    sortOrder: "desc",
    filePath: "",
    deliveryFileName: "",
    deliveryRef: "",
    content: "",
    statuses: selectedStatuses,
    notifications: selectedNotifications,
    recordTypes: selectedRecordTypes,
    associations: selectedAssociations,
    trailers: selectedTrailers,
    createdDateFrom: undefined,
    createdDateTo: undefined
};

export const useFileRecordListingFilters = () => {
    const filtersService = useMemo(() => listFiltersService<FileRecordListingFilters>(defaultFilters), []);
    const location = useLocation();
    const history = useHistory();
    const { listingConfig } = useContext(FileRecordListingContext);

    const [filters, setFilters] = useState<FileRecordListingFilters>(() => {
        const savedFilters = filtersService.getSavedFilters(location.search);

        return { ...savedFilters, pageSize: listingConfig.pageSize ?? savedFilters.pageSize };
    });

    useEffect(() => {
        filtersService.saveFilters(location.pathname, history, filters);
    }, [location.pathname, history, filters]);

    const setSort = (sort: ListTableSort) => {
        setFilters((prevFilters: FileRecordListingFilters) => ({
            ...prevFilters,
            sortBy: sort.by,
            sortOrder: sort.order,
            page: 1
        }));
    };

    const setPage = (page: number) => {
        setFilters((prevFilters: FileRecordListingFilters) => ({ ...prevFilters, page }));
    };

    const setPageSize = (pageSize: number): void => {
        setFilters((prevFilters: FileRecordListingFilters) => ({ ...prevFilters, pageSize, page: 1 }));
    };

    const setFilePath = (filePath?: string): void => {
        setFilters((prevFilters: FileRecordListingFilters) => ({ ...prevFilters, filePath, page: 1 }));
    };

    const setDeliveryRef = (deliveryRef?: string): void => {
        setFilters((prevFilters: FileRecordListingFilters) => ({ ...prevFilters, deliveryRef, page: 1 }));
    };

    const setDeliveryFileName = (deliveryFileName?: string): void => {
        setFilters((prevFilters: FileRecordListingFilters) => ({ ...prevFilters, deliveryFileName, page: 1 }));
    };

    const setContent = (content?: string): void => {
        setFilters((prevFilters: FileRecordListingFilters) => ({ ...prevFilters, content, page: 1 }));
    };

    const setStatuses = (statuses?: number[]): void => {
        setFilters((prevFilters: FileRecordListingFilters) => ({ ...prevFilters, statuses, page: 1 }));
    };

    const setNotifications = (notifications?: number[]): void => {
        setFilters((prevFilters: FileRecordListingFilters) => ({
            ...prevFilters,
            notifications,
            page: 1
        }));
    };

    const setRecordTypes = (recordTypes?: number[]): void => {
        setFilters((prevFilters: FileRecordListingFilters) => ({
            ...prevFilters,
            recordTypes,
            page: 1
        }));
    };

    const setAssociations = (associations?: number[]): void => {
        setFilters((prevFilters: FileRecordListingFilters) => ({
            ...prevFilters,
            associations,
            page: 1
        }));
    };

    const setTrailers = (trailers?: number[]): void => {
        setFilters((prevFilters: FileRecordListingFilters) => ({
            ...prevFilters,
            trailers,
            page: 1
        }));
    };

    const setCreatedDateFrom = (date?: Date): void => {
        setFilters((prevFilters: FileRecordListingFilters) => ({ ...prevFilters, createdDateFrom: date, page: 1 }));
    };

    const setCreatedDateTo = (date?: Date): void => {
        setFilters((prevFilters: FileRecordListingFilters) => ({ ...prevFilters, createdDateTo: date, page: 1 }));
    };

    const clearFilters = (): void => {
        setFilters(defaultFilters);
    };

    const statusOptions = selectedStatuses
        .concat(FileRecordStatus.QcDone)
        .map((status) => ({ key: status, text: depascalize(FileRecordStatus[status]) }));

    const recordTypeOptions = selectedRecordTypes.map((type) => ({
        key: type,
        text: depascalize(RecordType[type])
    }));

    const notificationsOptions = selectedNotifications.map((item) => ({
        key: item,
        text: depascalize(FileRecordBuzzNotification[item])
    }));

    const associationsOptions = selectedAssociations.map((item) => ({
        key: item,
        text: depascalize(FileRecordAssociation[item])
    }));

    const trailersOptions = selectedTrailers.map((type) => ({
        key: type,
        text: depascalize(TrailerFilter[type])
    }));

    return {
        filters,
        setSort,
        setPage,
        setPageSize,
        setFilePath,
        setDeliveryRef,
        setDeliveryFileName,
        setStatuses,
        setContent,
        setNotifications,
        setRecordTypes,
        setAssociations,
        setTrailers,
        setCreatedDateFrom,
        setCreatedDateTo,
        clearFilters,
        statusOptions,
        notificationsOptions,
        associationsOptions,
        recordTypeOptions,
        trailersOptions
    };
};
